import React, { createContext, useContext, useMemo } from 'react';
import api from 'src/config/api';
import jwtDecode from 'jwt-decode';
import Token from '../../interfaces/Token';
import { useCookies } from 'react-cookie';

const AuthContext = createContext<AuthProps>(undefined!);

type LoginFunction = (login: string, password: string) => Promise<LoginResponse>;

type LoginResponse = {
  token: string;
  refreshToken: string;
};

interface AuthProps {
  token?: Token;
  authenticated: boolean;
  fetch: AuthFetch;
  login: LoginFunction;
  logout: () => void;
}

type AuthFetch = (endpoint: string, options: RequestInit) => Promise<Response>;

let refreshTokenPromise: undefined | Promise<string> = undefined;

const fetchWithToken = (endpoint: string, options: RequestInit, token: string): Promise<Response> =>
  fetch(endpoint, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

const useProvideAuth = (): AuthProps => {
  const [{ accessToken }, setAccessTokenCookie, removeAccessToken] = useCookies<
    'accessToken',
    { accessToken?: string }
  >(['accessToken']);
  const [{ refreshToken }, setRefreshTokenCookie, removeRefreshToken] = useCookies<
    'refreshToken',
    { refreshToken?: string }
  >(['refreshToken']);

  const decodeToken: Token | undefined = useMemo(() => {
    try {
      return accessToken ? jwtDecode<Token>(accessToken) : undefined;
    } catch (e) {
      return undefined;
    }
  }, [accessToken]);

  const logout = () => {
    removeRefreshToken('refreshToken', { path: '/' });
    removeAccessToken('accessToken', { path: '/' });
  };

  const fetchRefreshToken = async (): Promise<string> => {
    try {
      if (!refreshToken) {
        throw new Error('Aucun refresh-token fourni');
      }

      const { endpoint, ...options } = api.employee.refreshToken(refreshToken);

      const response = await fetch(endpoint, options);

      if ([401, 403].includes(response.status)) {
        throw new Error("Echec lors de l'actualisation du token d'authentification");
      }

      const data = await response.json();

      setAccessTokenCookie('accessToken', data.token, { path: '/' });
      setRefreshTokenCookie('refreshToken', data.refreshToken, { path: '/' });

      return data.token;
    } catch (e) {
      logout();
      throw new Error(e.message);
    }
  };

  const exclusiveRefreshToken = async () => {
    if (!refreshTokenPromise) {
      refreshTokenPromise = fetchRefreshToken().then((response) => {
        refreshTokenPromise = undefined;
        return response;
      });
    }

    return await refreshTokenPromise;
  };

  const isAuthenticatedResponse = (response: Response): boolean => ![401, 403].includes(response.status);

  const authFetch: AuthFetch = async (endpoint, options = {}) => {
    if (!accessToken) {
      throw new Error('Authentification requise.');
    }

    const response = await fetchWithToken(endpoint, options, accessToken);

    if (isAuthenticatedResponse(response)) {
      return response;
    }

    try {
      const newToken = await exclusiveRefreshToken();
      const response = await fetchWithToken(endpoint, options, newToken);

      if (![401, 403].includes(response.status)) {
        return response;
      }
    } catch (e) {}

    throw new Error('Authentification requise.');
  };

  const login: LoginFunction = async (username: string, password: string) => {
    const { endpoint, ...options } = api.employee.getToken(username, password);

    const response = await fetch(endpoint, options);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.title);
    }

    setRefreshTokenCookie('refreshToken', data.refreshToken, { path: '/' });
    setAccessTokenCookie('accessToken', data.token, { path: '/' });

    return data;
  };

  return {
    authenticated: Boolean(accessToken),
    token: decodeToken,
    fetch: authFetch,
    login,
    logout,
  };
};

const AuthProvider: React.FC = ({ children }) => {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider };
export default useAuth;
